import { createFeatureSelector, createSelector } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';

import * as Objects from '@core/objects';
import * as Access from '@app/models/access';
import * as Search from '@app/models/search';
import { VersionShareItem } from '@core/models/version-share.model';

import { LogbookEventFilterTitlesList } from '@app/models/logbook-events.model';
// export interface ProjectLoaded {
//     id: number;
//     project: Request;
//     status: HttpStatus;
// }
/**
 * New logbook section in store to set filters for search in logbook
 * 
 */
interface LogbookAvailableFilters {
    users: Search.SearchAvailableFilters;
    events: Search.SearchAvailableFilters;
}

export interface LogbookSearch {
    active: boolean;
    term: string;
    // sort: string;
    // limit: number;
    filters: Array<Search.SearchAppliedFilter>;
    // query: string;
}

interface LogbookResults {
    // next_page: number;
    // final: boolean;
    status: number;
    items: Array<Objects.LogbookEvent>;
}

interface ProjectLogbook {
    events: Array<Objects.LogbookEvent>;
    available_filters:  LogbookAvailableFilters;
    search: LogbookSearch;
    search_query_params: Search.SearchQueryParams;
    results: LogbookResults;
}


// in snake style for help to find
interface ProjectsErrors {
    load_project: HttpErrorResponse,
    load_project_files: HttpErrorResponse,
    load_project_origin_version: HttpErrorResponse,
    load_project_messages: HttpErrorResponse,
    load_project_related_versions: HttpErrorResponse,
    load_project_uploaded_versions: HttpErrorResponse,
    upload_project_files: HttpErrorResponse,
    set_active_project_file: HttpErrorResponse,
    set_project_assigned: HttpErrorResponse,
    set_project_due: HttpErrorResponse,
    set_project_status: HttpErrorResponse,
    set_project_info: HttpErrorResponse,
    set_project_labels: HttpErrorResponse,
    load_editor_assigners: HttpErrorResponse,
    load_editor_access: HttpErrorResponse,
    load_project_files_notes_counter: HttpErrorResponse,
    load_editor_workflow_transitions: HttpErrorResponse,
    load_editor_current_workflow_transitions: HttpErrorResponse, // not sure
    load_header: HttpErrorResponse;
    load_events: HttpErrorResponse;
}

export interface ProjectUpdateActionsStatus {
    updateLabelsStatus: number;        // Add status for update project tags
}

// careful!!
// multiple calls after load related groups not work well 
// with mergeMap calls in effect loadProjectVersionGroupVersions$ 
export interface VersionsRelatedFeedbacks {
    items: Array<VersionShareItem>;
    feedbackAccess?: boolean;
}

export interface VersionGroupItem {
    versions: Array<Objects.Version>;
    id: string;
}

interface ProjectViewer {
    project: Objects.Request;
    files: Array<Objects.AmsFile>;
    files_notes_counter: Array<Objects.AnnotationsCounter>;
    messages: Array<Objects.Message>;
    origin_version: Objects.Version;
    // version_groups: Array<Objects.Related>;
    // version_groups_versions: Array<Objects.RelatedVersions>;
    related_versions: Array<Objects.RelatedVersions>;
    versions_feedbacks: VersionsRelatedFeedbacks; // not used yet
    uploaded_versions: Array<Objects.Version>;
    header: Objects.BasicRequest;
}

interface ProjectEditor {
    assigners: Array<Objects.User>;
    access: Access.AccessProject;
    workflow_transitions: Array<Objects.WorkflowTransition>;
    current_workflow_transitions: Array<Objects.WorkflowTransition>;
    actionsStatus: ProjectUpdateActionsStatus;
}

export interface Projects {
    logbook: ProjectLogbook;
    editor:  ProjectEditor;
    view: ProjectViewer;
    errors: ProjectsErrors;
}

export const initialState: Projects = {
    //status: new SettingsLicensesStatus,
    // project: null,
    // files: [],
    // origin_version: null,

    editor: {
        assigners: [],
        access: null,
        workflow_transitions: null,
        current_workflow_transitions: null,
        actionsStatus: {
            updateLabelsStatus: 0          // Initial status
        }
    },
    view: {
        project: null,
        files: [],
        files_notes_counter: null,
        messages: null,
        origin_version: null,
        // version_groups: null,
        // version_groups_versions: [],
        related_versions: null,
        versions_feedbacks: {
            items: [],
            feedbackAccess: null
        },
        uploaded_versions: null,
        header: null
    },
    logbook: {
        events: null,
        available_filters : {
            events: {
                key: "event",
                items: LogbookEventFilterTitlesList
            },
            users: {
                key: "user",
                items: []
            }
        },
        search: {
            active: false,
            term: "",
            // sort: "",
            // limit: 50,
            filters: [],
            // query: null
        },
        search_query_params: null,
        results: {
            // next_page: 1,
            // final: false,
            status: Search.SearchHttpStatus.INITIAL,
            items: null
        },
    },
    errors: {
        load_project: null,
        load_project_files: null,
        load_project_origin_version: null,
        load_project_messages: null, 
        // load_project_version_groups: null,
        load_project_related_versions: null,
        load_project_uploaded_versions: null, 
        upload_project_files: null,
        set_project_assigned: null, 
        set_project_due: null, 
        set_project_status: null,
        set_project_labels: null, 
        set_project_info: null, 
        set_active_project_file: null,
        load_editor_assigners: null,
        load_editor_access: null,
        load_project_files_notes_counter: null,
        load_editor_workflow_transitions: null,
        load_editor_current_workflow_transitions: null,
        load_header: null,
        load_events: null,
    }
};

const projectsFeatureSelector = createFeatureSelector('projects');

// Selectors
export const viewSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.view
);

export const projectSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.view.project
);

export const projectFilesSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.view.files
);

export const projectMessagesSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.view.messages
);

export const projectOriginVersionSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.view.origin_version
);

// export const projectVersionGroupsSelector = createSelector(
//     projectsFeatureSelector,
//     (state: Projects) => state.view.version_groups
// );
// all
// export const projectVersionGroupsVersionsSelector = createSelector(
//     projectsFeatureSelector,
//     (state: Projects) => state.view.version_groups_versions
// );

// single group
// export const projectVersionGroupVersionsSelector = (groupId: string) => createSelector(
//     projectsFeatureSelector,
//     (state: Projects) => 
//         state.view.version_groups_versions.filter((vr: Objects.RelatedVersions) => groupId == vr.id)
// );

export const projectRelatedVersionsSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.view.related_versions
);

export const projectUploadedVersionsSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.view.uploaded_versions
);

export const editorAssignersSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.editor.assigners
);

export const editorWorkflowTransitionsSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.editor.workflow_transitions
);

export const editorCurrentWorkflowTransitionsSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.editor.current_workflow_transitions
);

export const selectUpdateLabelsStatus = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.editor.actionsStatus.updateLabelsStatus
);  

export const editorAccessAllSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.editor.access
);

export const projectFilesNotesCounterSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.view.files_notes_counter
);

export const projectHeaderSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.view.header
);

/**
 * Logbook selectors for events and searck
 */
export const projectLogbookSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.logbook
);

export const projectLogbookAvailableFilterEventsSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.logbook.available_filters.events
);

export const projectLogbookAvailableFilterUsersSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.logbook.available_filters.users
);

export const projectLogbookResultItemsSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.logbook.results.items
);

export const  projectLogbookSearchFiltersSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.logbook.search.filters
);

export const projectLogbookSearchTermSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.logbook.search.term
);

export const projectLogbookSearchActiveSelector = createSelector(
    projectsFeatureSelector,
    (state: Projects) => state.logbook.search.active
); 



export const projectVersionsFeedbacksSelector = (versions: Objects.Version[]) =>
    createSelector(
        projectsFeatureSelector,
        (state: Projects) => {
            if (!state || !state.view.versions_feedbacks.items) {
                return []; // or any default value based on your use case
            }

            const versionIds = versions.map((version) => version.id);

            return state.view.versions_feedbacks.items.filter((vf: VersionShareItem) =>
                versionIds.includes(vf.version.id)
            );
        }
    );

export const editorAccessSelector = (key: string) => 
    createSelector(
        projectsFeatureSelector,
        (state: Projects) => state.editor.access!=null && state.editor.access.hasOwnProperty(key) ? state.editor.access[key] : false
);

export const errorSelector = (key: string) => 
    createSelector(
        projectsFeatureSelector,
        (state: Projects) => state.errors.hasOwnProperty(key) ? state.errors[key] : null
    );